import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'
import { DeviceLimits } from '@/types/programs-new/programs-new'
import { match } from 'ts-pattern'

export type DeviceLimitResult = {
  vehicle: boolean
  thermostat: boolean
  charger: boolean
  behavioral: boolean
  energySystem: boolean
  totalDevices: boolean
  limitsMet: boolean
}

const MeetsLimit = (count: number, min: number | null, max: number | null) => {
  return (min ?? -Infinity) <= count && count <= (max ?? Infinity)
}

function UserMeetsDeviceMinimumLimits(
  vehicles: number,
  thermostats: number,
  chargers: number,
  energySystems: number,
  // User can only enroll in one behavioral device
  behavioral: boolean,
  limits: DeviceLimits,
): DeviceLimitResult {
  const totalDevices = vehicles + thermostats + chargers + energySystems
  const limitRequirements = {
    vehicle:
      !limits.vehicle || !limits.vehicle.min || vehicles >= limits.vehicle.min,
    thermostat:
      !limits.thermostat ||
      !limits.thermostat.min ||
      thermostats >= limits.thermostat.min,
    charger:
      !limits.charger || !limits.charger.min || chargers >= limits.charger.min,
    energySystem:
      !limits.energy_system ||
      !limits.energy_system.min ||
      chargers >= limits.energy_system.min,
    // If a minimum is set, just check that the user is enrolled behaviorally
    behavioral: (limits.behavioral?.min ?? 0) > 0 ? behavioral : true,
    totalDevices:
      !limits.total_devices ||
      !limits.total_devices.min ||
      totalDevices >= limits.total_devices.min,
  }

  return {
    ...limitRequirements,
    limitsMet:
      limitRequirements.vehicle &&
      limitRequirements.charger &&
      limitRequirements.thermostat,
  }
}

function SpaceRemainingForDevice(
  deviceType: DeviceType,
  typeEnrollments: number,
  totalEnrollments: number,
  deviceLimits: DeviceLimits | null,
): number {
  if (!deviceLimits) {
    return Infinity
  }

  const typeLimit = match(deviceType)
    .with('vehicle', () => deviceLimits.vehicle)
    .with('thermostat', () => deviceLimits.thermostat)
    .with('vehicleCharger', () => deviceLimits.charger)
    .with('energySystem', () => deviceLimits.energy_system)
    .with('home', () => deviceLimits.behavioral)
    .exhaustive()

  const typeSpaceRemaining =
    (typeLimit?.max ?? Infinity) - typeEnrollments >= 0
      ? (typeLimit?.max ?? Infinity) - typeEnrollments
      : 0

  const totalSpaceRemaining =
    (deviceLimits.total_devices?.max ?? Infinity) - totalEnrollments >= 0
      ? (deviceLimits.total_devices?.max ?? Infinity) - totalEnrollments
      : 0

  return Math.min(typeSpaceRemaining, totalSpaceRemaining)
}

function UserWithinDeviceLimits(
  vehicles: number,
  thermostats: number,
  chargers: number,
  energySystems: number,
  // User can only enroll in one behavioral device
  behavioral: boolean,
  limits: DeviceLimits | null,
): DeviceLimitResult {
  if (!limits) {
    return {
      vehicle: true,
      thermostat: true,
      charger: true,
      behavioral: true,
      energySystem: true,
      totalDevices: true,
      limitsMet: true,
    }
  }

  const totalDevices = vehicles + thermostats + chargers + energySystems

  const limitRequirements = {
    vehicle:
      !limits.vehicle ||
      MeetsLimit(vehicles, limits.vehicle.min, limits.vehicle.max),
    thermostat:
      !limits.thermostat ||
      MeetsLimit(thermostats, limits.thermostat.min, limits.thermostat.max),
    charger:
      !limits.charger ||
      MeetsLimit(chargers, limits.charger.min, limits.charger.max),
    behavioral: MeetsLimit(
      behavioral ? 1 : 0,
      limits.behavioral?.min ?? null,
      limits.behavioral?.max ?? null,
    ),
    energySystem:
      !limits.energy_system ||
      MeetsLimit(
        energySystems,
        limits.energy_system.min,
        limits.energy_system.max,
      ),
    totalDevices:
      !limits.total_devices ||
      MeetsLimit(
        totalDevices,
        limits.total_devices.min,
        limits.total_devices.max,
      ),
  }

  return {
    ...limitRequirements,
    limitsMet:
      limitRequirements.vehicle &&
      limitRequirements.charger &&
      limitRequirements.behavioral &&
      limitRequirements.thermostat &&
      limitRequirements.energySystem &&
      limitRequirements.totalDevices,
  }
}

function DeviceLimitsAlertText(
  deviceLimits: DeviceLimits | null,
): null | string {
  if (!deviceLimits) {
    return null
  }

  const devices = [
    'charger',
    'thermostat',
    'vehicle',
    'energy_system',
    'total_devices',
  ] as const

  const requirementList = devices.flatMap((device) => {
    const limits = deviceLimits[device]
    if (!limits) return []

    const entries = []
    if (limits.min && limits.max && limits.min !== limits.max) {
      entries.push(
        `between ${limits.min} and ${limits.max} ${deviceLimitToReadable(
          device,
          limits.max,
        )}`,
      )
      return entries
    }

    if (limits.min) {
      entries.push(
        `at least ${limits.min} ${deviceLimitToReadable(device, limits.min)}`,
      )
      if (limits.min === limits.max) {
        return entries // Short-circuit if we don't really need a max
      }
    }
    if (limits.max) {
      entries.push(
        `up to ${limits.max} ${deviceLimitToReadable(device, limits.max)
          .split('_')
          .join(' ')
          .trim()}`,
      )
    }
    return entries
  })

  if (requirementList.length === 0) return null

  if (requirementList.length > 1) {
    requirementList[requirementList.length - 1] = `and ${
      requirementList[requirementList.length - 1]
    }`
  }

  return requirementList.length === 2
    ? requirementList.join(' ')
    : requirementList.join(', ')
}

function deviceLimitToReadable(deviceName: string, amount: number): string {
  // First step is to correctly pluralize the device name -- i.e, if there's 2 of "vehicle-charger",
  // we want to end up with "2 vehicle chargers"
  let pluralizedText = deviceName
  if (amount === 1 && deviceName[deviceName.length - 1] === 's') {
    pluralizedText = deviceName.slice(0, deviceName.length - 1)
  } else if (deviceName[deviceName.length - 1] !== 's' && amount > 1) {
    pluralizedText = `${pluralizedText}s`
  }

  // Now, we're just gonna clean up other parts of our string...
  const readable = pluralizedText.split('_').join(' ').trim()

  // ... and do some special case stuff for 'total device' limits, as we just want those to read
  // as "devices/device"
  if (readable.toLowerCase().includes('total device')) {
    return readable.split(' ')?.[1] ?? readable
  }
  return readable
}

const DeviceLimitHelpers = {
  UserWithinDeviceLimits,
  DeviceLimitsAlertText,
  UserMeetsDeviceMinimumLimits,
  SpaceRemainingForDevice,
}

export default DeviceLimitHelpers
